
import React from "react";
import { Profile } from "@/services/profiles/types";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { MapPin, Microscope, Building2, BookOpen } from "lucide-react";

interface ProfileCardProps {
  profile: Profile;
}

const ProfileCard: React.FC<ProfileCardProps> = ({ profile }) => {
  const navigate = useNavigate();
  
  const displayName = profile.display_username || profile.username || "Anonymous User";
  
  // Generate initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };
  
  const viewProfile = () => {
    navigate(`/community/profile/${profile.id}`);
  };
  
  const startConversation = () => {
    // Use "/conversation/" path for consistent navigation
    navigate(`/conversation/${profile.id}`);
    console.log("Starting conversation with user:", profile.id);
  };

  return (
    <Card className="h-full flex flex-col">
      <CardContent className="pt-6 flex-grow">
        <div className="flex items-start gap-4">
          <Avatar className="h-12 w-12">
            <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
          </Avatar>
          
          <div className="space-y-1 flex-grow">
            <h3 className="font-medium text-lg">{displayName}</h3>
            
            {profile.university && (
              <div className="flex items-center text-sm text-muted-foreground">
                <Building2 className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
                <span className="truncate">{profile.university}</span>
              </div>
            )}
            
            {profile.lab && (
              <div className="flex items-center text-sm text-muted-foreground">
                <Microscope className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
                <span className="truncate">{profile.lab}</span>
              </div>
            )}
            
            {(profile.city || profile.country) && (
              <div className="flex items-center text-sm text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
                <span className="truncate">
                  {[profile.city, profile.country].filter(Boolean).join(", ")}
                </span>
              </div>
            )}
            
            {profile.expertise && (
              <div className="flex items-start text-sm text-muted-foreground mt-2">
                <BookOpen className="h-3.5 w-3.5 mr-1 mt-0.5 flex-shrink-0" />
                <span className="line-clamp-2">{profile.expertise}</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="flex gap-2 pt-0">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={viewProfile}
          className="flex-1"
        >
          View Profile
        </Button>
        <Button 
          size="sm" 
          onClick={startConversation}
          className="flex-1"
        >
          Message
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ProfileCard;
